/**
 * Utility functions for file operations
 */

/**
 * Set of file extensions that are considered images
 */
export const imageExtensions = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'ico',
  'webp',
  'svg',
  'bmp',
  'tiff',
  'psd',
  'ai',
  'eps',
  'raw',
  'heic',
  'heif',
  'avif',
  'jfif',
  'jp2',
  'jpx',
  'tga',
  'xcf',
  'dng',
  'cr2',
  'cr3',
  'nef',
  'arw',
]);

/**
 * Set of file extensions that are considered binary (non-text) files.
 * Includes images, archives, media, fonts, data files, and more.
 */
export const binaryExtensions = new Set([
  ...imageExtensions,
  // Non-image binary types
  'pdf',
  'exe',
  'dll',
  'bin',
  'zip',
  'tar',
  'gz',
  '7z',
  'rar',
  'pkg',
  'dmg',
  'iso',
  'xz',
  'bz2',
  'tgz',
  'cab',
  // Archives and Executables
  'deb',
  'rpm',
  'msi',
  'app',
  'apk',
  'ipa',
  // Media
  'mp3',
  'mp4',
  'avi',
  'mov',
  'wmv',
  'flv',
  'wav',
  'ogg',
  'm4a',
  'm4v',
  'webm',
  'mkv',
  'aac',
  '3gp',
  'mpg',
  'mpeg',
  'vob',
  'swf',
  'rm',
  'rmvb',
  'mid',
  'midi',
  'wma',
  'asf',
  'flac',
  'm3u',
  'm3u8',
  // Fonts
  'woff',
  'woff2',
  'ttf',
  'otf',
  'eot',
  'pfm',
  'pfb',
  'afm',
  // Data and Cache Files
  'db',
  'sqlite',
  'sqlite3',
  'class',
  'pyc',
  'pyo',
  'pyd',
  'so',
  'dylib',
  'pkl',
  'pickle',
  'npy',
  'npz',
  'mat',
  'parquet',
  'arrow',
  'hdf5',
  'h5',
  'idx',
  'pb',
  'tfrecords',
  'onnx',
  'ptc',
  'pth',
  'weights',
  'dat',
  'sav',
  'mdb',
  'frm',
  'qvf',
  'gbr',
  'rdata',
  'rds',
  // IDE and System Files
  'suo',
  'pdb',
  'idb',
  // Stored lowercase so case-insensitive lookups in `isBinaryFile` match.
  'ds_store',
  'thumbs.db',
  'lnk',
  'sys',
  'tmp',
  'temp',
  'cache',
  'bak',
  'log',
  'dmp',
  'crash',
  'stackdump',
  // Rails encrypted credentials (e.g. `config/credentials/*.yml.enc`):
  // opaque ciphertext that the AI reviewer cannot reason about.
  'enc',
  // Office and Documents
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'odt',
  'ods',
  'odp',
  'pages',
  'numbers',
  'key',
  'indd',
  'qxd',
  // 3D and Design
  'stl',
  'obj',
  'fbx',
  'blend',
  'max',
  'ma',
  'mb',
  'c4d',
  '3ds',
  'skp',
  'dwg',
  'dxf',
  'step',
  'stp',
  'iges',
  'igs',
]);

/**
 * Determines if a file is an image based on its extension
 *
 * @param filename - The filename to check
 * @returns boolean indicating if the file is an image
 */
export function isImageFile(filename: string): boolean {
  const extension = filename.split('.').pop()?.toLowerCase();
  return !!extension && imageExtensions.has(extension);
}

/**
 * Determines if a file is a binary (non-text) file based on its extension or
 * basename. Includes images, archives, executables, media, fonts, data dumps,
 * IDE artifacts, office documents, and 3D/CAD assets — none of which the AI
 * reviewer can reason about meaningfully.
 *
 * Both the extension and the basename are checked because `binaryExtensions`
 * mixes the two: most entries are extensions (`'parquet'`, `'bak'`), but a
 * few are full basenames (`'thumbs.db'`, `'ds_store'`). The basename check
 * runs first so files whose basename is in the set are caught directly,
 * even if their extension would also match a generic data-extension entry.
 *
 * Extensionless files (e.g. `scripts/db`, `bin/log`) are NOT matched against
 * the basename or extension paths even when their bare name overlaps with an
 * extension entry like `db`/`log`/`bak` — a basename direct-match requires a
 * `.` in the name, and the extension fallback only fires when the basename
 * has a non-leading dot.
 *
 * @param filename - The filename to check
 * @returns boolean indicating if the file is a binary asset
 */
export function isBinaryFile(filename: string): boolean {
  const basename = filename.split('/').pop()?.toLowerCase();
  if (!basename) {
    return false;
  }
  // Direct basename match (e.g. `thumbs.db`). Requires a `.` in the name so
  // extensionless files like `scripts/db` or `bin/log` aren't falsely matched
  // against extension entries (`db`, `log`, `bak`, ...).
  if (basename.includes('.') && binaryExtensions.has(basename)) {
    return true;
  }
  // Extension lookup. For dotfiles like `.DS_Store`, treat everything after
  // the leading dot as the extension so the dot-stripped form (`ds_store`)
  // in `binaryExtensions` still matches.
  const dotIdx = basename.lastIndexOf('.');
  if (dotIdx < 0) {
    return false;
  }
  return binaryExtensions.has(basename.slice(dotIdx + 1));
}

// List of filenames and patterns that are typically auto-generated and should be auto-collapsed
// Based on GitHub Linguist's generated.rb + additional common patterns
// See: https://github.com/github-linguist/linguist/blob/master/lib/linguist/generated.rb
export const MACHINE_GENERATED_FILES = [
  // =============================================================================
  // Git internals - should never be reviewed or scanned
  // =============================================================================
  '.git/',

  // =============================================================================
  // Package manager lock files (from GitHub Linguist + extras)
  // =============================================================================
  // Generic lock file patterns - catches all dependency lock files
  '*.lock',
  '*.lockb',

  // Non-.lock package manager files (kept because *.lock doesn't match them)
  'package-lock.json',
  'packages.lock.json',
  'npm-shrinkwrap.json',
  'pnpm-lock.yaml',
  'shrinkwrap.yaml',
  '.pnp.cjs',
  '.pnp.loader.mjs',
  '.pnp.js',
  'Cargo.toml.orig',
  'go.sum',
  'go.work.sum',
  'Package.resolved',
  '.terraform.lock.hcl',
  '*requirements-lock*',
  'gradle.lockfile',
  'pylock.toml',

  // Build tool directories
  '.gradle/',
  '.terraform/',
  '.serverless/',
  '.pulumi/',

  // =============================================================================
  // Minified assets
  // =============================================================================
  '.min.js',
  '.min.css',
  // Source-map files of every flavor (`.js.map`, `.css.map`, `.d.ts.map`,
  // tool-specific maps such as Doxygen `__incl.map`). Production data
  // showed `*.js.map`/`*.css.map` was missing the bulk of `.map` traffic
  // (mostly `.d.ts.map`) and that none of the leaked-through `.map`
  // file_runs ever produced a violation.
  '*.map',

  // =============================================================================
  // Common build outputs
  // =============================================================================
  'dist/',
  'build/',
  '.d.ts',
  '.nyc_output/',

  // =============================================================================
  // Node.js/Frontend
  // =============================================================================
  'node_modules/',
  '.cache/',
  '.next/',
  '.nuxt/',
  '.output/',
  'coverage/',
  '.eslintcache',
  '.stylelintcache',
  '*.bundle.js',
  '*.chunk.js',
  'htmlcov/',

  // =============================================================================
  // Static site generators
  // =============================================================================
  '.svelte-kit/',
  '.astro/',
  '.docusaurus/',

  // =============================================================================
  // Java/Kotlin/Android
  // =============================================================================
  'gen/',
  'out/',
  'generated/',
  'R.java',
  'BuildConfig.java',

  // Build wrappers (from GitHub Linguist)
  'gradlew',
  'gradlew.bat',
  'mvnw',
  'mvnw.cmd',

  // =============================================================================
  // Python
  // =============================================================================
  '__pycache__/',
  '*.pyc',
  '*.pyo',
  '*.pyd',
  '.Python',
  'env/',
  'venv/',
  '.env/',
  '.venv/',
  'ENV/',
  'env.bak/',
  'venv.bak/',

  // =============================================================================
  // Ruby
  // =============================================================================
  '.bundle/',
  'vendor/bundle',
  // Sorbet RBI files (generated by Tapioca)
  '*.rbi',

  // =============================================================================
  // Go
  // =============================================================================
  'vendor/',
  'Godeps/',
  '*.pb.go',
  '*.pb.gw.go',
  '*.stringer.go',
  '*_gen.go',
  '*_generated.go',
  'zz_generated*.go',
  '*_mock.go',

  // =============================================================================
  // Rust
  // =============================================================================
  '*.pb.rs',
  '*_pb.rs',
  '*_grpc.rs',
  '*.capnp.rs',
  '*_fb.rs',
  'schema.rs',
  'sqlx-data.json',
  '*_bindings.rs',
  'bindings.rs',
  'src/generated/',
  'src/gen/',
  'OUT_DIR/',
  // SQLx query cache (from GitHub Linguist)
  '.sqlx/',

  // =============================================================================
  // Dart / Flutter
  // =============================================================================
  '.dart_tool/',
  '*.g.dart',
  '*.freezed.dart',
  '*.mocks.dart',

  // =============================================================================
  // C# / .NET
  // =============================================================================
  'bin/',
  'obj/',
  'Debug/',
  'Release/',
  'packages/debug/',
  'packages/release/',
  '*.Designer.cs',
  '*.designer.cs',
  '*.designer.vb',
  '*.g.cs',
  '*.g.i.cs',
  '*.AssemblyInfo.cs',
  '*.Generated.cs',
  // SpecFlow feature files (from GitHub Linguist)
  '*.feature.cs',

  // =============================================================================
  // Swift / Objective-C / Xcode (from GitHub Linguist)
  // =============================================================================
  'Pods/',
  'Carthage/Build/',
  'xcuserdata/',
  '*.xcworkspace',
  '*.xcodeproj',
  'DerivedData/',
  '*.swiftinterface',
  '*.swiftmodule',
  '*.g.swift',
  '*.generated.swift',
  // Xcode specific (from GitHub Linguist)
  '*.nib',
  '*.xcworkspacedata',
  '*.xcuserstate',

  // =============================================================================
  // C / C++
  // =============================================================================
  '*.pb.cc',
  '*.pb.h',
  '*.moc.cpp',
  '*.pb.c',
  '*.pb-c.h',
  // gRPC C++ (from GitHub Linguist)
  '*.grpc.pb.cc',
  '*.grpc.pb.h',

  // =============================================================================
  // Prisma (TypeScript)
  // =============================================================================
  'node_modules/.prisma/',
  '.prisma/client/',
  'prisma/generated/',

  // =============================================================================
  // Supabase
  // =============================================================================
  'supabase/types/',
  'supabase/schema.graphql',
  'supabase.ts',

  // =============================================================================
  // GraphQL (expanded - includes *.graphqls for generated schemas)
  // =============================================================================
  '__generated__/',
  '*.generated.ts',
  '*.generated.tsx',
  '*.graphql.ts',
  '*.graphql.d.ts',
  // GraphQL schema files - commonly auto-generated from protobuf, databases, etc.
  '*.graphqls',
  // Apollo/Relay
  'schema.graphql',
  'introspection.json',

  // =============================================================================
  // OpenAPI / Swagger
  // =============================================================================
  'openapi/generated/',
  'swagger.json',
  'openapi.json',
  'openapi.yaml',

  // =============================================================================
  // Apache Thrift (from GitHub Linguist)
  // =============================================================================
  '*_types.py',
  '*_constants.py',

  // =============================================================================
  // Test fixtures and snapshots
  // =============================================================================
  '__snapshots__/',
  '*.snap',
  '*.golden.*',
  // VCR cassettes (from GitHub Linguist)
  '*.vcr.yml',

  // Note: `*.csv`, `*.tsv`, `*.jsonl`, `*.ndjson`, `*.patch`, and
  // `*.diff` are intentionally NOT blanket-skipped here. Production data
  // showed real findings on small instances of these files (Odoo access
  // CSVs, localization CSVs, integration-test JSONL fixtures, applied
  // git patches). They are instead size-capped per extension via
  // `PER_TYPE_DIFF_LINE_CAPS` in `should-skip-file.ts`, which catches
  // the dump-shaped cases (zip-code datasets, eval datasets, lead-gen
  // exports) without silencing legitimate small changes.

  // =============================================================================
  // General TypeScript/JavaScript helpers
  // =============================================================================
  '*.pb.ts',
  '*.g.ts',
  '*.gen.ts',
  '*.g.tsx',
  '*.gen.tsx',
  // TypeScript incremental build cache, regenerated by `tsc --incremental`
  '*.tsbuildinfo',

  // =============================================================================
  // Misc auto-generated artifacts
  // =============================================================================
  // DVC (Data Version Control) pointer files — small YAML stubs that point at
  // tracked data blobs; contents are content-addressable hashes only.
  '*.dvc',
  // `.gitkeep` placeholders — empty-by-convention files used to preserve empty
  // directories. Nothing to review.
  '*.gitkeep',
  // Doxygen / repo-tree dumps and similar tree snapshots.
  '*.tree',
  // PowerShell module manifest data files (PSD1) — generated metadata.
  '*.psd1',
  // MSBuild props files — auto-generated build configuration.
  '*.props',
  // Generated default config snapshots (e.g. `*.default.json`/`config.default`).
  '*.default',
  // Auto-generated reference/word lists (e.g. spell-check, dictionary dumps).
  '*.list',
  // Generated rule artifacts (e.g. lint-rule fixtures, codegen rule files).
  '*.rule',
  // Graphite stack metadata files.
  '*.graphite',
  // Kiro IDE generated artifacts.
  '*.kiro',
  // Environment files for production-like targets are typically auto-generated
  // by deploy pipelines (Vercel/Netlify pull, CI bake) and contain only opaque
  // values. The base `.env`/`.env.local` patterns remain reviewable.
  '*.env.production',
  '*.env.staging',
  '*.env.prod',

  // =============================================================================
  // Database ORM snapshots (auto-generated)
  // =============================================================================
  'Migrations/*_*.cs',
  // Drizzle ORM
  'drizzle/meta/',
  '*_snapshot.json',
  // Prisma's lock file is generated, but migration SQL files are reviewable.
  'prisma/migrations/migration_lock.toml',

  // =============================================================================
  // Game development (from GitHub Linguist)
  // =============================================================================
  // Unity3D
  '*.meta',
  // Game Maker Studio
  '*.yy',
  '*.yyp',

  // =============================================================================
  // Zephir (from GitHub Linguist)
  // =============================================================================
  '*.zep.c',
  '*.zep.h',
  '*.zep.php',

  // =============================================================================
  // Delphi (from GitHub Linguist)
  // =============================================================================
  '*_tlb.pas',

  // =============================================================================
  // MySQL (from GitHub Linguist)
  // =============================================================================
  '*.frm',

  // =============================================================================
  // Common cache directories
  // =============================================================================
  '.tmp/',
  'temp/',

  // =============================================================================
  // Common IDE/editor files
  // =============================================================================
  '.idea/',
  '.vscode/',
  '.vs/',
  '*.swp',
  '*.swo',
  '.DS_Store',

  // =============================================================================
  // PostScript (from GitHub Linguist)
  // =============================================================================
  '*.ps',
  '*.eps',
  '*.pfa',

  // =============================================================================
  // R documentation (from GitHub Linguist)
  // =============================================================================
  '*.Rd',

  // =============================================================================
  // Haxe output (from GitHub Linguist)
  // =============================================================================
  '*.hxgen.js',
  '*.hxgen.py',

  // =============================================================================
  // Visual Studio 6.0 (from GitHub Linguist)
  // =============================================================================
  '*.dsp',

  // =============================================================================
  // jOOQ (from GitHub Linguist)
  // =============================================================================
  '**/jooq/**/*.java',

  // =============================================================================
  // Templates and Bazel build files — skipped for review COST, not because they
  // are generated. Production timeout analysis showed these extensions have a
  // high abort-per-file ratio (they repeatedly exhaust the per-file review time
  // budget and get retried) while rarely producing posted violations; their
  // contents are typically large, dense, and low-signal for inline review.
  // =============================================================================
  '*.tmpl',
  '*.gotmpl',
  '*.bzl',
];

/**
 * Checks if a file is likely machine-generated based on its filename
 * @param filename The filename to check
 * @returns boolean indicating if the file is likely machine-generated
 */
export const isMachineGeneratedFile = (filename: string): boolean => {
  return MACHINE_GENERATED_FILES.some((pattern) => {
    // Check for exact match first
    if (filename === pattern) {
      return true;
    }

    const isDir = pattern.endsWith('/');
    // Remove the trailing slash if it's a directory pattern
    const cleanedPattern = isDir ? pattern.slice(0, -1) : pattern;

    // Handle wildcard patterns (patterns that start with *)
    if (pattern.startsWith('*.')) {
      const extension = pattern.slice(1);
      const parts = filename.split('/');
      const basename = parts[parts.length - 1] || '';
      return basename.endsWith(extension);
    }

    const regexPattern = cleanedPattern
      .replace(/\./g, '\\.')
      .replace(/\*/g, '.*')
      .replace(/\//g, '\\/');

    if (isDir) {
      // For directories, match if the pattern is a complete segment
      return new RegExp(
        `(^${regexPattern}$)|(^${regexPattern}\\\/)|(\\\/${regexPattern}$)|(\\\/${regexPattern}\\\/)`,
      ).test(filename);
    } else if (pattern.includes('/') && !isDir) {
      // For patterns with path separators (like 'supabase/schema.graphql')
      // We check if the filename ends with this pattern or contains it as a segment
      return filename.endsWith(pattern) || filename.includes(`${pattern}/`);
    } else {
      // For file patterns, match if the pattern appears at the end of the filename
      const parts = filename.split('/');
      const basename = parts[parts.length - 1] || '';
      return new RegExp(`${regexPattern}$`).test(basename);
    }
  });
};
